import * as THREE from 'three';
import type { Flame } from './Ambience';
import { fxUniforms, quadGeometry } from './common';

/**
 * The steady body of every brazier and torch flame: an upright billboard whose shader draws a
 * flickering teardrop of fire, eroded by scrolling noise. The particle tongues rise out of it,
 * so the fire reads continuous rather than as separate puffs. One draw for all flames, no
 * per-frame uploads.
 */
export function buildFlameCores(scene: THREE.Scene, flames: Flame[]) {
  const list = flames.filter((f) => f.fire && f.fireSize && f.radius < 0.2);
  if (!list.length) return;
  const geometry = quadGeometry();
  const at = new Float32Array(list.length * 4), col = new Float32Array(list.length * 4);
  const c = new THREE.Color();
  list.forEach((f, i) => {
    const s = f.fireSize!;
    at.set([f.pos.x, f.pos.y - s * 0.35, f.pos.z, s], i * 4);
    c.set(f.color);
    col.set([c.r, c.g, c.b, i * 1.37], i * 4);
  });
  geometry.setAttribute('iAt', new THREE.InstancedBufferAttribute(at, 4));
  geometry.setAttribute('iCol', new THREE.InstancedBufferAttribute(col, 4));
  geometry.instanceCount = list.length;
  const material = new THREE.ShaderMaterial({
    uniforms: { uTime: fxUniforms.uTime },
    transparent: true, depthWrite: false, blending: THREE.AdditiveBlending,
    vertexShader: /* glsl */ `
      attribute vec4 iAt, iCol;
      varying vec2 vUv; varying vec3 vCol; varying float vSeed;
      void main() {
        vUv = uv; vCol = iCol.rgb; vSeed = iCol.w;
        // upright billboard: turns about the vertical axis only, so the flame never tips over
        vec3 toCam = cameraPosition - iAt.xyz; toCam.y = 0.0;
        vec3 right = normalize(vec3(toCam.z, 0.0, -toCam.x));
        vec3 p = iAt.xyz + right * position.x * iAt.w * 1.5 + vec3(0.0, (position.y + 0.5) * iAt.w * 3.0, 0.0);
        gl_Position = projectionMatrix * viewMatrix * vec4(p, 1.0);
      }`,
    fragmentShader: /* glsl */ `
      uniform float uTime; varying vec2 vUv; varying vec3 vCol; varying float vSeed;
      float h2(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
      float vn(vec2 p) { vec2 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f);
        return mix(mix(h2(i), h2(i + vec2(1, 0)), f.x), mix(h2(i + vec2(0, 1)), h2(i + vec2(1, 1)), f.x), f.y); }
      void main() {
        float t = uTime + vSeed * 7.0, y = vUv.y;
        // the tip sways and the whole body breathes
        float sway = (sin(t * 3.1) * 0.6 + sin(t * 5.3 + 1.0) * 0.4) * 0.09 * y * y;
        float n = vn(vec2(vUv.x * 4.0, y * 3.0 - t * 2.6)) * 0.6 + vn(vec2(vUv.x * 9.0, y * 6.0 - t * 4.1)) * 0.4;
        float x = (vUv.x - 0.5 - sway) * 2.0;
        // teardrop: round at the base, tapering to a tip that the noise tears apart
        float width = sqrt(max(0.0, 1.0 - y)) * smoothstep(0.0, 0.18, y) * (0.85 + 0.1 * sin(t * 7.0));
        float body = 1.0 - smoothstep(width * 0.55, width, abs(x) + (n - 0.5) * 0.35 * y);
        body *= smoothstep(0.0, 0.25, n - y * 0.75 + 0.35);
        float core = (1.0 - smoothstep(0.0, width * 0.5, abs(x))) * (1.0 - smoothstep(0.15, 0.6, y));
        vec3 colour = mix(vCol * 1.2, vec3(1.6, 1.25, 0.8), core);
        gl_FragColor = vec4(colour * body * (0.85 + 0.15 * sin(t * 11.0)), 1.0);
      }`,
  });
  const mesh = new THREE.Mesh(geometry, material);
  mesh.name = 'flameCores'; mesh.frustumCulled = false; mesh.renderOrder = 4;
  scene.add(mesh);
}
